import { useEffect, useMemo, useRef } from "react";
import { UseInfiniteQueryResult } from "@tanstack/react-query";

import { Notification } from "@/queries/notifications/useNotifications";

interface UseInfiniteScrollNotificationsProps {
  notificationsQuery: UseInfiniteQueryResult<{
    success: boolean;
    notifications: Notification[];
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
    next_num: number | null;
    prev_num: number | null;
  }>;
  dismissedIds: number[];
}

export const useInfiniteScrollNotifications = ({
  notificationsQuery,
  dismissedIds,
}: UseInfiniteScrollNotificationsProps) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = notificationsQuery;

  // Flatten all pages of notifications
  const allNotifications = useMemo(
    () => data?.pages.flatMap(page => page.notifications) ?? [],
    [data],
  );

  // Filter out dismissed notifications
  const visibleNotifications = useMemo(
    () => allNotifications.filter(notification => !dismissedIds.includes(notification.id)),
    [allNotifications, dismissedIds],
  );

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const currentTarget = observerTarget.current;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    visibleNotifications,
    allNotifications,
    observerTarget,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
  };
};
